import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  signUp as amplifySignUp,
  getCurrentUser,
  confirmSignUp as amplifyConfirmSignUp,
  resetPassword as amplifyResetPassword,
  confirmResetPassword as amplifyConfirmResetPassword,
  resendSignUpCode as amplifyResendSignUpCode,
  confirmSignIn as amplifyConfirmSignIn,
  setUpTOTP as amplifySetUpTOTP,
  verifyTOTPSetup as amplifyVerifyTOTPSetup,
  updateMFAPreference,
  fetchMFAPreference,
  fetchAuthSession,
} from 'aws-amplify/auth';

export type UserRole = 'admin' | 'viewer';

interface AuthUser {
  username: string;
  userId: string;
  groups: string[];
  role: UserRole;
}

interface SignInResult {
  /** true = fully signed in; false = MFA code required */
  isSignedIn: boolean;
  /** Set when MFA is required — caller should prompt for TOTP code */
  nextStep?: string;
}

interface MfaStatus {
  enabled: boolean;
  preferred: 'totp' | 'none';
}

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  isAdmin: boolean;
  isViewer: boolean;
  signIn: (username: string, password: string) => Promise<SignInResult>;
  confirmMfaSignIn: (code: string) => Promise<void>;
  signUp: (username: string, password: string, email: string) => Promise<void>;
  confirmSignUp: (username: string, code: string) => Promise<void>;
  resendSignUpCode: (username: string) => Promise<void>;
  resetPassword: (username: string) => Promise<void>;
  confirmResetPassword: (username: string, code: string, newPassword: string) => Promise<void>;
  signOut: () => Promise<void>;
  // MFA setup
  setupTOTP: () => Promise<{ qrUri: string; secretKey: string }>;
  verifyTOTPSetup: (code: string) => Promise<void>;
  enableMFA: () => Promise<void>;
  disableMFA: () => Promise<void>;
  getMfaStatus: () => Promise<MfaStatus>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** Extract groups from the Cognito ID token. */
async function getUserGroups(): Promise<string[]> {
  try {
    const session = await fetchAuthSession();
    const payload = session.tokens?.idToken?.payload;
    if (payload && payload['cognito:groups']) {
      const groups = payload['cognito:groups'];
      return Array.isArray(groups) ? groups.map(String) : [String(groups)];
    }
    return [];
  } catch {
    return [];
  }
}

/** Derive role from groups — admin if in admin group, otherwise viewer. */
function deriveRole(groups: string[]): UserRole {
  return groups.includes('admin') ? 'admin' : 'viewer';
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const checkUser = useCallback(async () => {
    try {
      const currentUser = await getCurrentUser();
      const groups = await getUserGroups();
      const role = deriveRole(groups);
      setUser({
        username: currentUser.username,
        userId: currentUser.userId,
        groups,
        role,
      });
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkUser();
  }, [checkUser]);

  const signIn = async (username: string, password: string): Promise<SignInResult> => {
    const result = await amplifySignIn({ username, password });
    if (result.isSignedIn) {
      await checkUser();
      return { isSignedIn: true };
    }
    // MFA challenge — user needs to enter TOTP code
    if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
      return { isSignedIn: false, nextStep: 'TOTP' };
    }
    return { isSignedIn: true };
  };

  const confirmMfaSignIn = async (code: string) => {
    const result = await amplifyConfirmSignIn({ challengeResponse: code });
    if (result.isSignedIn) {
      await checkUser();
    }
  };

  const signUp = async (username: string, password: string, email: string) => {
    await amplifySignUp({
      username,
      password,
      options: { userAttributes: { email } },
    });
  };

  const confirmSignUp = async (username: string, code: string) => {
    await amplifyConfirmSignUp({ username, confirmationCode: code });
  };

  const resendSignUpCode = async (username: string) => {
    await amplifyResendSignUpCode({ username });
  };

  const resetPassword = async (username: string) => {
    await amplifyResetPassword({ username });
  };

  const confirmResetPassword = async (username: string, code: string, newPassword: string) => {
    await amplifyConfirmResetPassword({ username, confirmationCode: code, newPassword });
  };

  const setupTOTP = async () => {
    const totpSetup = await amplifySetUpTOTP();
    const secretKey = totpSetup.sharedSecret;
    const qrUri = totpSetup.getSetupUri('SignalDeck').toString();
    return { qrUri, secretKey };
  };

  const verifyTOTPSetup = async (code: string) => {
    await amplifyVerifyTOTPSetup({ code });
  };

  const enableMFA = async () => {
    await updateMFAPreference({ totp: 'PREFERRED' });
  };

  const disableMFA = async () => {
    await updateMFAPreference({ totp: 'DISABLED' });
  };

  const getMfaStatus = async (): Promise<MfaStatus> => {
    try {
      const pref = await fetchMFAPreference();
      const enabled = pref.enabled?.includes('TOTP') ?? false;
      const preferred = pref.preferred === 'TOTP' ? ('totp' as const) : ('none' as const);
      return { enabled, preferred };
    } catch {
      return { enabled: false, preferred: 'none' };
    }
  };

  const signOut = async () => {
    await amplifySignOut();
    setUser(null);
  };

  // ── Session inactivity timeout ────────────────────────────
  const TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
  const WARNING_MS = TIMEOUT_MS - 2 * 60 * 1000; // warn at 28 min
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const warningRef = useRef<ReturnType<typeof setTimeout>>();
  const warningFiredRef = useRef(false);

  const resetInactivityTimer = useCallback(() => {
    if (!user) return;

    // Clear existing timers
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);
    warningFiredRef.current = false;

    // Warning toast at 28 min
    warningRef.current = setTimeout(() => {
      warningFiredRef.current = true;
      window.dispatchEvent(new CustomEvent('session-warning'));
    }, WARNING_MS);

    // Auto sign-out at 30 min
    timeoutRef.current = setTimeout(() => {
      signOut();
      window.dispatchEvent(new CustomEvent('session-expired'));
    }, TIMEOUT_MS);
  }, [user]);

  useEffect(() => {
    if (!user) return;

    const events = ['mousedown', 'keydown', 'touchstart', 'scroll', 'mousemove'];
    // Throttle resets to once per 30 seconds to avoid excessive timer churn
    let lastReset = Date.now();
    const handler = () => {
      if (Date.now() - lastReset > 30_000) {
        lastReset = Date.now();
        resetInactivityTimer();
      }
    };

    events.forEach((e) => window.addEventListener(e, handler, { passive: true }));
    resetInactivityTimer(); // start the clock

    return () => {
      events.forEach((e) => window.removeEventListener(e, handler));
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningRef.current) clearTimeout(warningRef.current);
    };
  }, [user, resetInactivityTimer]);

  const isAdmin = user?.role === 'admin';
  const isViewer = user?.role === 'viewer';

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAdmin,
        isViewer,
        signIn,
        confirmMfaSignIn,
        signUp,
        confirmSignUp,
        resendSignUpCode,
        resetPassword,
        confirmResetPassword,
        signOut,
        setupTOTP,
        verifyTOTPSetup,
        enableMFA,
        disableMFA,
        getMfaStatus,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
